// /operations — admin-facing platform capability coverage.
//
// The page is read-only and driven by platform/adminPlatformModel. It makes the
// external platform reference lens, live surfaces, backend gaps, and explicit
// external ownership visible from one model instead of scattering static tables.

import { Link } from 'react-router-dom';
import {
  ADMIN_MECHANISMS,
  STATUS_LABEL,
  STATUS_TONE,
  PLATFORM_REFERENCE_LENSES,
  type AdminMechanism,
  type AdminSurfaceStatus,
  type PlatformReferenceLens,
} from '../../platform/adminPlatformModel';
import './operations.css';

// 狀態 → 左側 accent 色條(與 pill tone 對齊)
const STATUS_RAIL: Record<AdminSurfaceStatus, string> = {
  live: 'accent-rail accent-rail--good',
  partial: 'accent-rail accent-rail--info',
  planned: 'accent-rail accent-rail--warn',
  blocked: 'accent-rail accent-rail--warn',
  external: 'accent-rail accent-rail--neutral',
};

function StatusPill({ status }: { status: AdminSurfaceStatus }) {
  return <span className={`pill pill-dot ${STATUS_TONE[status]}`}>{STATUS_LABEL[status]}</span>;
}

function EntryLink({ route }: { route?: string }) {
  if (!route) return <span className="text-tertiary">尚無入口</span>;
  return (
    <Link className="btn-ghost btn-sm" to={route}>
      開啟
    </Link>
  );
}

function MechanismTable({
  items,
  testId,
  muted,
}: {
  items: AdminMechanism[];
  testId: string;
  muted?: boolean;
}) {
  if (items.length === 0) {
    // 某分類清空(如 roadmap 全數落地)時,不留裸表頭 → 卡內迷你空狀態
    return (
      <div className="adm-inline-empty" data-testid={testId}>
        <span className="adm-inline-empty-icon">🗂️</span>
        <span>此分類目前沒有項目。</span>
      </div>
    );
  }

  return (
    <div
      className={`table-scroll${muted ? ' is-muted ops-external-scroll' : ''}`}
      data-testid={testId}
    >
      <table className="data-table ops-table">
        <thead>
          <tr>
            <th>機制</th>
            <th>狀態</th>
            <th>參考面</th>
            <th>管理工作</th>
            <th>契約 / 前置</th>
            <th>控制</th>
            <th aria-label="入口" />
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id} data-testid={`ops-${item.id}`}>
              <td className="ops-area">{item.area}</td>
              <td>
                <StatusPill status={item.status} />
              </td>
              <td>{item.referenceDomain ?? '—'}</td>
              <td>{item.job}</td>
              <td className="mono ops-contract">{item.contract}</td>
              <td>{item.guard}</td>
              <td className="ops-entry">
                <EntryLink route={item.route} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReferenceLensCard({ item }: { item: PlatformReferenceLens }) {
  return (
    <article
      className={`card card--muted ${STATUS_RAIL[item.status]} ops-lens-card`}
      data-testid={`reference-lens-${item.id}`}
    >
      <div className="ops-lens-head">
        <h3>{item.domain}</h3>
        <StatusPill status={item.status} />
      </div>
      <dl className="ops-lens-body">
        <div>
          <dt>外部參考面</dt>
          <dd>{item.referenceSurface}</dd>
        </div>
        <div>
          <dt>LS Cloud 取捨</dt>
          <dd>{item.lsCloudDecision}</dd>
        </div>
        <div>
          <dt>下一步</dt>
          <dd>{item.nextStep}</dd>
        </div>
      </dl>
      <EntryLink route={item.route} />
    </article>
  );
}

export default function AdminMechanismsPage() {
  const liveCount = ADMIN_MECHANISMS.live.length;
  const roadmapCount = ADMIN_MECHANISMS.roadmap.length;
  const externalCount = ADMIN_MECHANISMS.external.length;

  return (
    <div className="ops-page" data-testid="ops-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">能力覆蓋</h1>
          <p className="page-subtitle">
            以完整 IoT 平台視角盤點 admin surface：App / Home、Device、Product、Cloud、Data、Governance。
          </p>
        </div>
      </div>

      <div className="kpi-strip" aria-label="管理機制統計">
        <div className="kpi-card kpi-card--hero" data-testid="ops-kpi-live">
          <span className="kpi-label">可操作 / 已接端點</span>
          <span className="kpi-value">{liveCount}</span>
          <span className="kpi-foot">已接後端、可直接操作的管理面</span>
        </div>
        <div className="kpi-card accent-rail accent-rail--warn" data-testid="ops-kpi-roadmap">
          <span className="kpi-label">待端點或決策</span>
          <span className="kpi-value kpi-value--md">{roadmapCount}</span>
          <span className="kpi-foot">卡端點或業主決策</span>
        </div>
        <div className="kpi-card kpi-card--secondary" data-testid="ops-kpi-external">
          <span className="kpi-label">明確歸屬外部</span>
          <span className="kpi-value">{externalCount}</span>
          <span className="kpi-foot">由其他系統或維運手冊承擔</span>
        </div>
      </div>

      <section className="ops-section">
        <div className="section-head">
          <h2 className="section-title">外部參考取捨</h2>
          <span className="section-meta">參考 · 採納 · 延後 · 不採</span>
        </div>
        <div className="ops-lens-grid" data-testid="reference-lens-grid">
          {PLATFORM_REFERENCE_LENSES.map((item) => (
            <ReferenceLensCard key={item.id} item={item} />
          ))}
        </div>
      </section>

      <section className="ops-section">
        <div className="section-head">
          <h2 className="section-title">已可操作</h2>
          <span className="section-meta">已接端點、可直接操作</span>
        </div>
        <MechanismTable items={ADMIN_MECHANISMS.live} testId="ops-live" />
      </section>

      <section className="ops-section">
        <div className="section-head">
          <h2 className="section-title">待開 / 待決策</h2>
          <span className="section-meta">不得先做假形狀 UI</span>
        </div>
        <MechanismTable items={ADMIN_MECHANISMS.roadmap} testId="ops-roadmap" />
      </section>

      <section className="ops-section">
        <div className="section-head">
          <h2 className="section-title">不在主控台內</h2>
          <span className="section-meta">歸屬其他系統或維運手冊</span>
        </div>
        <MechanismTable items={ADMIN_MECHANISMS.external} testId="ops-external" muted />
      </section>
    </div>
  );
}
